import { Form } from 'antd';
import { FormField, FormTextArea, FormTitle } from '../../../components/form/FormBasic';
import { FormFieldTypeEnum } from '../../../enums/FormFieldTypeEnum';
import { FormInstance, useWatch } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import DefaultButton from '../../../components/buttons/DefaultButton';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import CanvasComponent from '../../../components/form/CanvasComponent';
import { useState } from 'react';
import FormModelComponent from '../../../components/model/FormModelComponent';
import { XSquare } from 'react-bootstrap-icons';
import {
  REGEX_ALL_ALLOWED_SAFE_FILTER,
  REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER,
} from '../../../constants/regex';

interface RespondentFormProps {
  form: FormInstance;
  handleSubmit?: () => void;
  handleCancel?: () => void;
  disabled: boolean;
  isViewMode?: boolean;
  handleBack?: () => void;
}

const SubmitFuelDispenserVerificationForm: React.FC<RespondentFormProps> = ({
  form,
  handleSubmit,
  handleCancel,
  disabled,
  isViewMode = false,
  handleBack,
}) => {
  const { t } = useTranslation(['price','inspection']);

  const observations = useWatch('observations', form);
  const nameOfAuditor = useWatch('nameOfAuditor', form);
  const [hasDrawnMetrologySignature, setHasDrawnMetrologySignature] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const updateRepresentativeSignatureStatus = (drew: boolean, dataUrl: string) => {
    form.setFieldValue('hasRepresentativeSignature', drew);
    form.setFieldValue('signatureOfServiceStationRep', dataUrl);
  };

  const updateOfficerSignatureStatus = (drew: boolean, dataUrl: string) => {
    form.setFieldValue('hasOfficerSignature', drew);
    form.setFieldValue('signatureOfAuditor', dataUrl);

    setHasDrawnMetrologySignature(drew);
  };

  const toggleModal = () => {
    isModalOpen ? setIsModalOpen(false) : setIsModalOpen(true);
  };

  const hasAllFieldsValue = !!observations && !!nameOfAuditor && hasDrawnMetrologySignature;
  return (
    <div className={`m-0 w-full ${isViewMode ? 'p-2' : 'p-8'}`}>
      <div className="mb-4 space-y-2">
        <h2 className="border-b border-gray-200 pb-3 text-xl font-semibold text-gray-900">
          {t('inspection:signatureDetails')}
        </h2>
      </div>
      <Form
        form={form}
        name="respondentDataForm"
        layout="vertical"
        disabled={disabled || isViewMode}
      >
        <FormTextArea
          name="observations"
          label={t('inspection:observations')}
          rules={[
            { required: true, message: t('observationsRequired') },
            {
              validator: (_, value) => {
                if (value && value.trim() === '') {
                  return Promise.reject(t('inspection:generalObservationsSpaceValidation'));
                }
                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                // Allow letters, numbers, space, punctuation and special characters
                // const regex = /^[A-Za-z0-9ÁÉÍÓÚáéíóúÑñÜü\s,.\-!?;:()"'&@#$%^*+=/]+$/;
                const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                return regex.test(value)
                  ? Promise.resolve()
                  : Promise.reject(t('inspection:invalidInput'));
              },
            },
            {
              validator: (_, value) => {
                if (!value || value.trim() === '') {
                  return Promise.resolve();
                }

                const trimmedLength = value.replace(/\s/g, '').length;
                if (trimmedLength < 3) {
                  return Promise.reject(t('inspection:observationsMinValidation'));
                }
                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (!value || value.trim() === '') {
                  return Promise.resolve();
                }

                const trimmedLength = value.replace(/\s/g, '').length;
                if (trimmedLength > 200) {
                  return Promise.reject(t('inspection:observationsMaxValidation'));
                }
                return Promise.resolve();
              },
            },
          ]}
          placeholder={t('observationsFieldPlaceholder')}
          disabled={false || isViewMode}
          isRequired={false}
        />

        <FormField
          name="nameOfAuditor"
          label={t('nameOfAuditor')}
          rules={[
            { required: true, message: t('nameOfAuditorFieldRequired') },
            {
              validator: (_, value) => {
                if (value && value.trim() === '') {
                  return Promise.reject(t('nameOfAuditorSpaceValidation'));
                }
                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                // Allows letters, space and fullstop
                const regex = REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER;
                return regex.test(value)
                  ? Promise.resolve()
                  : Promise.reject(t('inspection:invalidInput'));
              },
            },
            {
              validator: (_, value) => {
                if (!value || value.trim() === '') {
                  return Promise.resolve();
                }

                const trimmedLength = value.replace(/\s/g, '').length;
                if (trimmedLength < 3) {
                  return Promise.reject(t('nameOfAuditorMinValidation'));
                }
                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (!value || value.trim() === '') {
                  return Promise.resolve();
                }

                const trimmedLength = value.replace(/\s/g, '').length;
                if (trimmedLength > 100) {
                  return Promise.reject(t('nameOfAuditorMaxValidation'));
                }
                return Promise.resolve();
              },
            },
          ]}
          placeholder={t('nameOfAuditorFieldPlaceholder')}
          disabled={false || isViewMode}
          type={FormFieldTypeEnum.INPUT}
          isRequired={true}
          allowLeadingTrailingSpaces={true}
        />

        {!isViewMode ? (
          <Form.Item
            name="signatureOfAuditor"
            label={t('signatureOfAuditorLabel')}
            rules={[{ required: false, message: t('signatureFieldRequired') }]}
          >
            <CanvasComponent updateSignatureStatus={updateOfficerSignatureStatus} />
          </Form.Item>
        ) : (
          <Form.Item label={t('signatureOfAuditorLabel')}>
            {form.getFieldValue('signatureOfAuditor') ? (
              <img
                src={form.getFieldValue('signatureOfAuditor')}
                alt={t('signatureOfAuditorLabel')}
                className="max-h-64 max-w-full rounded-lg border"
              />
            ) : (
              <p>{t('noSignature')}</p>
            )}
          </Form.Item>
        )}

        <FormField
          name="nameOfServiceStationRep"
          label={t('nameOfServiceStationRepresentative')}
          rules={[
            {
              validator: (_, value) => {
                if (value && value.trim() === '') {
                  return Promise.reject(t('nameOfServiceStationRepresentativeSpaceValidation'));
                }
                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                // Allows letters, space and fullstop
                const regex = REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER;
                return regex.test(value)
                  ? Promise.resolve()
                  : Promise.reject(t('inspection:invalidInput'));
              },
            },
            {
              validator: (_, value) => {
                if (!value || value.trim() === '') {
                  return Promise.resolve();
                }

                const trimmedLength = value.replace(/\s/g, '').length;
                if (trimmedLength < 3) {
                  return Promise.reject(t('nameOfServiceStationRepMinValidation'));
                }
                return Promise.resolve();
              },
            },
            {
              validator: (_, value) => {
                if (!value || value.trim() === '') {
                  return Promise.resolve();
                }

                const trimmedLength = value.replace(/\s/g, '').length;
                if (trimmedLength > 100) {
                  return Promise.reject(t('nameOfServiceStationRepMaxValidation'));
                }
                return Promise.resolve();
              },
            },
          ]}
          placeholder={
            !isViewMode ? t('nameOfServiceStationRepresentativeFieldPlaceholder') : t('notGiven')
          }
          disabled={false || isViewMode}
          type={FormFieldTypeEnum.INPUT}
          isRequired={false}
          allowLeadingTrailingSpaces={true}
        />
        {!isViewMode ? (
          <Form.Item
            name="signatureOfServiceStationRep"
            label={t('serviceStationRepresentativeSignatureLabel')}
            rules={[{ required: false, message: t('signatureFieldRequired') }]}
          >
            <CanvasComponent updateSignatureStatus={updateRepresentativeSignatureStatus} />
          </Form.Item>
        ) : (
          <Form.Item label={t('serviceStationRepresentativeSignatureLabel')}>
            {form.getFieldValue('signatureOfServiceStationRep') ? (
              <img
                src={form.getFieldValue('signatureOfServiceStationRep')}
                alt={t('serviceStationRepresentativeSignatureLabel')}
                className="max-h-64 max-w-full rounded-lg border"
              />
            ) : (
              <p className="pl-2">{t('notGiven')}</p>
            )}
          </Form.Item>
        )}

        {!isViewMode && (
          <div className="mt-6 flex w-full flex-col justify-end gap-4 pt-4 md:ml-auto md:w-1/2 md:flex-row">
            <DefaultButton
              handleOnClick={() => {
                handleCancel?.();
              }}
              width="100%"
            >
              {t('cancel')}
            </DefaultButton>

            <SecondaryButton
              handleOnClick={() => {
                handleBack?.();
              }}
              width="100%"
            >
              {t('goBack')}
            </SecondaryButton>

            {hasAllFieldsValue ? (
              <PrimaryButton
                handleOnClick={(e) => {
                  e.preventDefault();
                  toggleModal();
                }}
                width="100%"
                disabled={disabled || isViewMode}
                hideBgGradient={true}
              >
                {t('submit')}
              </PrimaryButton>
            ) : (
              <SecondaryButton
                handleOnClick={(e) => {
                  e.preventDefault();
                  handleSubmit?.();
                }}
                width="100%"
                disabled={!hasAllFieldsValue || isViewMode}
              >
                {t('submit')}
              </SecondaryButton>
            )}
          </div>
        )}

        <>
          <FormModelComponent
            isOpen={isModalOpen}
            header={t('verificationSubmitModalTitle')}
            content={t('verificationSubmitModalConfirmaiton')}
            onClickedCancel={toggleModal}
            footerAction={{
              confimation: {
                label: t('yes'),
                onClick: handleSubmit ?? (() => {}),
              },
              cancel: {
                label: t('no'),
                onClick: toggleModal,
              },
            }}
            closeIcon={<XSquare className="text-primary-font-red" size={20} />}
            contentStyles="mb-4 px-2 max-h-[80vh] overflow-y-auto space-y-2"
          ></FormModelComponent>
        </>
      </Form>
    </div>
  );
};

export default SubmitFuelDispenserVerificationForm;
